import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { ChevronUpDownIcon, XMarkIcon, CheckIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useIsDisabledByPermission } from '../contexts/PermissionAccessContext';
import { useFieldShellDisplay } from '../contexts/DisplayPreferencesContext';

export interface MultiSelectOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
  isDivider?: boolean; // If true, renders as a divider line instead of an option
  isBold?: boolean; // If true, renders label in bold
  isHeader?: boolean; // If true, renders as a non-clickable section header
}

interface MultiSelectProps {
  options: MultiSelectOption[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  loading?: boolean; // Show loading state for async options
  countThreshold?: number; // Number of items to show before switching to count mode
  showClearOption?: boolean; // Show "All" option to clear all selections
  clearOptionLabel?: string; // Label for the clear option (default: "All")
  // Show a "Select All" row that ticks every option currently listed (the search
  // narrows it), so the user can then untick the one or two they don't want — the
  // way to say "everything except Barbour's Cut". Leaves the dropdown open.
  showSelectAllOption?: boolean;
  selectAllLabel?: string; // Label for the select-all row (default: "Select All")
  enableSearch?: boolean; // Enable search/filter functionality
  searchPlaceholder?: string; // Placeholder for search input
  size?: 'sm' | 'md'; // Size variant for compact styling
  dropdownDirection?: 'up' | 'down'; // Direction dropdown opens
  usePortal?: boolean; // Render dropdown in a portal to escape overflow:hidden containers
  preserveOrder?: boolean; // Preserve the order of options (don't sort alphabetically)
}

const MultiSelect: React.FC<MultiSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select multiple options...',
  className = '',
  disabled: disabledProp = false,
  loading = false,
  countThreshold = 3,
  showClearOption = false,
  clearOptionLabel = 'All',
  showSelectAllOption = false,
  selectAllLabel = 'Select All',
  enableSearch = false,
  searchPlaceholder = 'Search...',
  size = 'md',
  dropdownDirection = 'down',
  usePortal = false,
  preserveOrder = false,
}) => {
  const disabledByPermission = useIsDisabledByPermission();
  const disabled = disabledProp || disabledByPermission;
  // Field display prefs: plain => selection as text, no box/chevron; the class
  // marks this trigger as the box for the CSS-only options (tint, color, fade).
  const { plain: plainLocked, className: shellClass } = useFieldShellDisplay(disabled);

  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  // Effective direction: the prop, or an auto-flip up when the dropdown would
  // fall off the bottom of the viewport (estimate: search bar + max-h-60 list).
  const [openUpward, setOpenUpward] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const clickedInsideContainer = containerRef.current?.contains(target);
      const clickedInsideDropdown = dropdownRef.current?.contains(target);

      if (!clickedInsideContainer && !clickedInsideDropdown) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Calculate direction (auto-flip near the viewport bottom) + portal position.
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const estHeight = 320; // search bar + max-h-60 options list + padding
      const up = dropdownDirection === 'up'
        || (window.innerHeight - rect.bottom < estHeight && rect.top > estHeight);
      setOpenUpward(up);
      if (usePortal) {
        setDropdownPosition({
          top: up ? rect.top - 8 : rect.bottom + 8,
          left: rect.left,
          width: rect.width,
        });
      }
    }
  }, [isOpen, usePortal, dropdownDirection]);

  // Focus search input when dropdown opens and clear search when it closes
  useEffect(() => {
    if (isOpen && enableSearch && searchInputRef.current) {
      searchInputRef.current.focus();
    }
    if (!isOpen) {
      setSearchQuery('');
    }
  }, [isOpen, enableSearch]);

  const toggleOption = (optionValue: string) => {
    if (value.includes(optionValue)) {
      onChange(value.filter(v => v !== optionValue));
    } else {
      onChange([...value, optionValue]);
    }
  };

  const clearAll = () => {
    onChange([]);
  };

  const getDisplayText = () => {
    if (value.length === 0) {
      return placeholder;
    }

    if (value.length <= countThreshold) {
      // Show individual items
      return value
        .map(v => options.find(opt => opt.value === v)?.label || v)
        .join(', ');
    } else {
      // Show count mode: "2 items & +3"
      const firstItems = value.slice(0, 2);
      const remaining = value.length - 2;
      const itemsText = firstItems
        .map(v => options.find(opt => opt.value === v)?.label || v)
        .join(', ');
      return `${itemsText} & +${remaining}`;
    }
  };

  // Sort options: selected ones first, then alphabetically (unless preserveOrder is true)
  const sortedOptions = useMemo(() => {
    if (preserveOrder) return options;

    return [...options].sort((a, b) => {
      const aSelected = value.includes(a.value);
      const bSelected = value.includes(b.value);

      if (aSelected === bSelected) {
        return (a.label || '').localeCompare(b.label || '');
      }

      return aSelected ? -1 : 1;
    });
  }, [options, preserveOrder, value]);

  // Filter helper for search — applied inline in the JSX render
  const matchesSearch = (option: MultiSelectOption): boolean => {
    if (!enableSearch || !searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return String(option.label ?? '').toLowerCase().includes(q) || String(option.value ?? '').toLowerCase().includes(q);
  };

  /** The real (non-divider/header) options the dropdown is currently listing — what
   *  "Select All" acts on, so searching "Houston" and hitting it selects that subset
   *  and a second search can add another. */
  const listedValues = sortedOptions.filter(o => !o.isDivider && !o.isHeader && matchesSearch(o)).map(o => o.value);
  const allListedSelected = listedValues.length > 0 && listedValues.every(v => value.includes(v));

  /** Add every listed option to the selection, keeping anything already picked that the
   *  search hides. Stays open: the point is to untick one or two afterwards. */
  const selectAllListed = () => onChange(Array.from(new Set([...value, ...listedValues])));

  /** Toggle the first real (non-divider/header) option matching the search. */
  const selectFirstMatch = (): boolean => {
    const first = sortedOptions.filter(matchesSearch).find(o => !o.isDivider && !o.isHeader);
    if (!first) return false;
    toggleOption(first.value);
    return true;
  };

  // Search-box keys: Enter toggles the first match (stay open for more picks);
  // Tab toggles the first match when something was typed, then closes back to
  // the trigger (the portaled input has no sane natural next tab stop);
  // Escape just closes.
  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (selectFirstMatch()) setSearchQuery('');
    } else if (e.key === 'Tab') {
      e.preventDefault();
      if (searchQuery.trim()) selectFirstMatch();
      setIsOpen(false);
      buttonRef.current?.focus();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsOpen(false);
      buttonRef.current?.focus();
    }
  };

  // Trigger keys: typing a character opens the dropdown pre-seeded with it
  // (type-to-search without reaching for the mouse); arrows just open. Space is
  // left to the native button activation (plain open, no leading-space seed).
  const handleButtonKeyDown = (e: React.KeyboardEvent) => {
    if (disabled || isOpen) return;
    if (enableSearch && e.key.length === 1 && e.key !== ' ' && !e.metaKey && !e.ctrlKey && !e.altKey) {
      e.preventDefault();
      setSearchQuery(e.key);
      setIsOpen(true);
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setIsOpen(true);
    }
  };

  // Dropdown content
  const dropdownContent = (
    <div
      ref={dropdownRef}
      className={`${usePortal ? 'fixed' : 'absolute'} z-[9999] min-w-full w-max bg-white border border-gray-200 rounded-lg shadow-lg dark:bg-gray-900 dark:border-gray-700 ${!usePortal && openUpward ? 'bottom-full mb-2' : !usePortal ? 'mt-2' : ''}`}
      style={usePortal ? {
        top: openUpward ? 'auto' : dropdownPosition.top,
        bottom: openUpward ? `calc(100vh - ${dropdownPosition.top}px + 8px)` : 'auto',
        left: dropdownPosition.left,
        minWidth: dropdownPosition.width,
      } : undefined}
    >
      {/* Search Input */}
      {enableSearch && (
        <div className={`${size === 'sm' ? 'p-1' : 'p-2'} border-b border-gray-200 dark:border-gray-700`}>
          <input
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            placeholder={searchPlaceholder}
            className={`w-full ${size === 'sm' ? 'px-2 py-1 text-xs' : 'px-3 py-2 text-sm'} border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white dark:placeholder-gray-400`}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Options List */}
      <div className="max-h-60 p-1 space-y-0.5 overflow-y-auto">
        {/* Clear All Option */}
        {showClearOption && (
          <div
            onClick={() => {
              clearAll();
              setIsOpen(false);
            }}
            className={`${size === 'sm' ? 'py-1 px-2 text-xs' : 'py-2 px-4 text-sm'} w-full cursor-pointer hover:bg-blue-50 rounded-lg focus:outline-none focus:bg-blue-50 dark:hover:bg-blue-900/30 dark:focus:bg-blue-900/30 flex justify-between items-center text-blue-600 dark:text-blue-400 font-medium border-b border-gray-200 dark:border-gray-700 mb-1 whitespace-nowrap`}
          >
            <span>{clearOptionLabel}</span>
            {value.length === 0 && (
              <CheckIcon className="w-3.5 h-3.5" />
            )}
          </div>
        )}
        {/* Select All — everything the list currently shows, then untick the exceptions. */}
        {showSelectAllOption && !loading && listedValues.length > 0 && (
          <div
            role="button"
            aria-pressed={allListedSelected}
            onClick={selectAllListed}
            className={`${size === 'sm' ? 'py-1 px-2 text-xs' : 'py-2 px-4 text-sm'} w-full cursor-pointer hover:bg-blue-50 rounded-lg focus:outline-none focus:bg-blue-50 dark:hover:bg-blue-900/30 dark:focus:bg-blue-900/30 flex justify-between items-center text-blue-600 dark:text-blue-400 font-medium border-b border-gray-200 dark:border-gray-700 mb-1 whitespace-nowrap`}
          >
            <span>{searchQuery ? `${selectAllLabel} (${listedValues.length})` : selectAllLabel}</span>
            {allListedSelected && (
              <CheckIcon className="w-3.5 h-3.5" />
            )}
          </div>
        )}
        {loading ? (
          <div className="py-4 px-4 text-sm text-center text-muted flex items-center justify-center gap-2">
            <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Loading...
          </div>
        ) : sortedOptions.filter(matchesSearch).length === 0 ? (
          <div className="py-4 px-4 text-sm text-center text-muted">
            No options found
          </div>
        ) : (
          sortedOptions.filter(matchesSearch).map((option, index) => {
            // Render divider
            if (option.isDivider) {
              return (
                <div
                  key={`divider-${index}`}
                  className="border-b border-gray-200 dark:border-gray-700 my-1"
                />
              );
            }

            // Render section header
            if (option.isHeader) {
              return (
                <div
                  key={`header-${index}`}
                  className={`${size === 'sm' ? 'py-1 px-2 text-xs' : 'py-1.5 px-4 text-xs'} font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide`}
                >
                  {option.label}
                </div>
              );
            }

            const isSelected = value.includes(option.value);
            return (
              <div
                // Index-suffixed: option values aren't guaranteed unique (e.g. two custom
                // CSR groups can share an identical member-list value), and a duplicate
                // React key makes rows duplicate/drop. Keep keys unique per rendered row.
                key={`${option.value}-${index}`}
                onClick={() => toggleOption(option.value)}
                className={`${size === 'sm' ? 'py-1 px-2 text-xs' : 'py-2 px-4 text-sm'} w-full cursor-pointer hover:bg-gray-100 rounded-lg focus:outline-none focus:bg-gray-100 dark:hover:bg-gray-800 dark:focus:bg-gray-800 flex justify-between items-center whitespace-nowrap ${
                  isSelected ? 'text-heading' : 'text-gray-800 dark:text-gray-200'
                }`}
              >
                <div className="flex items-center gap-2">
                  {option.icon && <span className="flex-shrink-0">{option.icon}</span>}
                  <span className={option.isBold ? 'font-semibold' : ''}>{option.label}</span>
                </div>
                {isSelected && (
                  <CheckIcon className="w-3.5 h-3.5 text-blue-600 dark:text-blue-500" />
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      {/* Toggle Button */}
      <button
        ref={buttonRef}
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onKeyDown={handleButtonKeyDown}
        disabled={disabled}
        className={`relative ${size === 'sm' ? 'py-1 ps-2 pe-6 text-xs' : 'py-3 ps-4 pe-9 text-sm'} flex gap-x-2 text-nowrap w-full cursor-pointer bg-white border border-gray-400 rounded-lg text-start focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-900 dark:border-gray-500 dark:focus:ring-gray-600 ${
          disabled ? 'opacity-50 pointer-events-none' : ''
        } ${value.length === 0 ? 'text-gray-500' : 'text-heading'} ${shellClass}`}
        aria-expanded={isOpen}
      >
        <span className="truncate">{getDisplayText()}</span>
        {!plainLocked && (
          <div className={`absolute top-1/2 ${size === 'sm' ? 'end-1' : 'end-3'} -translate-y-1/2`}>
            <ChevronUpDownIcon className={`${size === 'sm' ? 'w-3 h-3' : 'w-4 h-4'} text-gray-500 dark:text-gray-500`} />
          </div>
        )}
      </button>

      {/* Dropdown - render in portal or inline. When inside a modal <dialog>
          (opened via showModal), portal into that dialog so the dropdown shares
          its top-layer stacking context — otherwise it renders beneath the dialog
          regardless of z-index (matches TypeAhead). Falls back to document.body. */}
      {isOpen && (usePortal
        ? createPortal(dropdownContent, containerRef.current?.closest('dialog') || document.body)
        : dropdownContent)}
    </div>
  );
};

export default MultiSelect;
